import { memo, useCallback } from 'react';
import { Dropdownlist } from '@/components/Dropdownlist';
import type { FormSearchPredicateData } from '@/services/api-types';
import type { FormFilterBaseProps } from './filter-base';

export interface RadioFilterProps extends FormFilterBaseProps {}

/**
 * Dropdown filter over a radio field's option list. Renders as a `Dropdownlist`
 * (not a radio button group) because this is a compact search surface, not a
 * form input — and `Dropdownlist` is what the surrounding native filters in
 * Records Search already use (e.g. the State picker), so the look matches.
 *
 * Emits `{op: 'EQ', value: selected}` when an option is picked, or `[]` when
 * the user clears the picker.
 */
export const RadioFilter = memo(function RadioFilter({
  field,
  predicates,
  onPredicatesChange,
}: RadioFilterProps) {
  const existing = predicates.find((p) => p.op === 'EQ');
  const currentValue = existing && typeof existing.value === 'string' ? existing.value : null;

  const handleChange = useCallback(
    (val: string | number | null) => {
      if (val == null || val === '') {
        onPredicatesChange([]);
        return;
      }
      const predicate: FormSearchPredicateData = {
        fieldKey: field.key,
        op: 'EQ',
        value: val as string,
      };
      onPredicatesChange([predicate]);
    },
    [field.key, onPredicatesChange],
  );

  const options = field.options ?? [];

  return (
    <div className="field">
      <label htmlFor={`form-filter-${field.key}`}>{field.label}</label>
      <Dropdownlist
        value={currentValue}
        items={options}
        sortItems={false}
        searchable={false}
        clearable
        placeholder={`Any ${field.label.toLowerCase()}`}
        onChange={handleChange}
      />
    </div>
  );
});
